import { useEffect, useState, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { LockedBadge } from './locked-badge';
import { useParticipateCta } from './participate-cta';
import type { NotEligibleReason } from './not-eligible';

interface StickyCtaProps {
  heroRef: RefObject<HTMLDivElement | null>;
  giftsRef: RefObject<HTMLDivElement | null>;
  finalRef: RefObject<HTMLDivElement | null>;
  balance: number | null;
  participationConfirmed: boolean;
  /** Не участвует в акции — панели нет: она ведёт только к кнопке или к балансу. */
  notEligible: NotEligibleReason | null;
}

/** Плавающая CTA на мобильных (`#stickyCta` макета) — видна, когда hero/подарки/финал вне экрана. */
export function StickyCta({ heroRef, giftsRef, finalRef, balance, participationConfirmed, notEligible }: StickyCtaProps) {
  const { t } = useTranslation('landing');
  const [visible, setVisible] = useState(false);
  const cta = useParticipateCta();

  useEffect(() => {
    const vis = { hero: true, gifts: false, final: false };
    const targets: [keyof typeof vis, HTMLElement | null][] = [
      ['hero', heroRef.current],
      ['gifts', giftsRef.current],
      ['final', finalRef.current],
    ];
    const io = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        const key = targets.find(([, el]) => el === entry.target)?.[0];
        if (key) vis[key] = entry.isIntersecting;
      }
      setVisible(!vis.hero && !vis.gifts && !vis.final);
    });
    for (const [, el] of targets) if (el) io.observe(el);
    return () => io.disconnect();
  }, [heroRef, giftsRef, finalRef]);

  const cls = visible ? 'stickycta show' : 'stickycta';

  if (notEligible) return null;

  // После подтверждения кнопки нет: панель — только баланс, вся ведёт к подаркам.
  if (participationConfirmed && balance !== null) {
    return (
      <a className={`${cls} balonly`} href="#gifts">
        <span className="lbl">
          <i className="ico i-goat" />
          <span>{t('stickyCta.labelPrefix')}&nbsp;</span>
          <b className="bal">{balance}</b>
          &nbsp;{t('stickyCta.labelSuffix', { count: balance })}
        </span>
        <span className="ar">→</span>
      </a>
    );
  }

  return (
    <div className={cls}>
      <div className="lbl">
        <i className="ico i-goat" />
        <LockedBadge />
      </div>
      <a href={cta.href} onClick={cta.onClick} className="btn">
        {t('stickyCta.cta')} →
      </a>
    </div>
  );
}
